import { Navigate } from "react-router-dom";
import { useClient, usePortal, type MemberRow } from "@app/portal-sdk";
import { Alert, AppBadge, DataTable, EmptyState, PageHeader, Stack } from "@app/ui-kit";
import { LoadingScreen } from "../components/LoadingScreen";
import { displayName, formatDate } from "../lib/format";
import { useApiQuery } from "../lib/useApiQuery";

/** Who has access to this client portal. Read-only; changes go through BKDP. */
export const Team = () => {
  const { api } = usePortal();
  const { client, canManage } = useClient();
  const members = useApiQuery<MemberRow[]>(
    () => (canManage ? api.request<MemberRow[]>(`/clients/${client.id}/members`) : Promise.resolve([])),
    [api, client.id, canManage]
  );

  if (!canManage) return <Navigate to={`/c/${client.slug}`} replace />;
  if (members.loading && !members.data) return <LoadingScreen />;

  const rows = members.data ?? [];

  return (
    <Stack spacing={4}>
      <PageHeader
        eyebrow={client.name}
        title="Team"
        subtitle="Everyone who can open this portal. To add or remove someone, contact BKDP."
      />
      {members.error && <Alert severity="error">{members.error}</Alert>}
      {rows.length === 0 ? (
        <EmptyState eyebrow="Team" title="No members yet" />
      ) : (
        <DataTable
          columns={[
            { key: "name", label: "Name" },
            { key: "email", label: "Email" },
            { key: "role", label: "Role" },
            { key: "status", label: "Status" },
            { key: "lastLogin", label: "Last sign-in" }
          ]}
          rows={rows.map((m) => ({
            name: displayName(m.user),
            email: m.user.email,
            role: m.role === "client_admin" ? "Client admin" : "Member",
            status: m.status === "active" ? <AppBadge label="Active" color="success" /> : <AppBadge label="Invited" color="info" />,
            lastLogin: formatDate(m.lastLoginAt, "never")
          }))}
        />
      )}
    </Stack>
  );
};
